import type { QrDrawModel } from './model';

export interface SvgImageSources {
  /** Data URL for the centre logo, or null to omit it. */
  readonly centerImageHref: string | null;
  /** Data URL stamped into every module, or null to use vector shapes. */
  readonly moduleImageHref: string | null;
}

/** Escapes the five characters that are unsafe inside an XML attribute. */
function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Serialises a draw model to a genuine vector SVG.
 *
 * The old "Download as SVG" button called `canvas.toDataURL()` and wrapped the
 * resulting PNG in an `<svg><image>` shell — a raster file with an `.svg`
 * extension that could not be scaled or edited. Because every shape is already
 * path data by the time it reaches here, real vector output is just
 * serialisation.
 */
export function renderToSvg(model: QrDrawModel, sources: SvgImageSources): string {
  const size = model.pixelSize;
  const parts: string[] = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${String(size)}" height="${String(size)}" viewBox="0 0 ${String(size)} ${String(size)}" shape-rendering="crispEdges">`,
    `<rect width="${String(size)}" height="${String(size)}" fill="${escapeAttribute(model.background)}"/>`,
  ];

  for (const layer of model.layers) {
    parts.push(
      `<path fill="${escapeAttribute(layer.fill)}" fill-rule="nonzero" d="${escapeAttribute(layer.d)}"/>`,
    );
  }

  if (sources.moduleImageHref !== null) {
    const href = escapeAttribute(sources.moduleImageHref);
    for (const box of model.imageModules) {
      parts.push(
        `<image href="${href}" x="${String(box.x)}" y="${String(box.y)}" width="${String(box.size)}" height="${String(box.size)}"/>`,
      );
    }
  }

  if (model.centerImage !== null && sources.centerImageHref !== null) {
    const { x, y, size: boxSize } = model.centerImage;
    parts.push(
      `<image href="${escapeAttribute(sources.centerImageHref)}" x="${String(x)}" y="${String(y)}" width="${String(boxSize)}" height="${String(boxSize)}" preserveAspectRatio="xMidYMid meet"/>`,
    );
  }

  parts.push('</svg>');
  return parts.join('');
}
